import type { CartItem, CartLine, CartModifierItem } from "@/shared/cart/model.types"
import type { BillingPreviewComboMode, BillingPreviewV1CartItem } from "@/domain/cart/billing-preview.types"
import { resolveModifierIdentity } from "@/domain/cart/cart.modifier-identity"

export { resolveModifierIdentity, type ResolvedModifierIdentity } from "@/domain/cart/cart.modifier-identity"

/**
 * Modifier quantity for billing preview — **must match** backend pricing mappers
 * (e.g. `BillingPreviewCartMapper` / billing service: `modifier.quantity ?? parentLine.quantity`).
 *
 * @contract billing-v1-preview-modifier-qty — keep in sync with server-side cart normalization.
 */
export function resolveModifierQuantity(
  mod: { quantity?: number | null },
  parentQty: number
): number {
  return mod.quantity ?? parentQty
}

/**
 * Merge rows that share the same modifier identity (`resolveModifierIdentity().primary_key`);
 * quantities are summed. Pure; returns new array.
 */
export function dedupeModifierGroupItems(items: CartModifierItem[]): CartModifierItem[] {
  const byKey = new Map<string, CartModifierItem>()
  for (const m of items) {
    const { primary_key } = resolveModifierIdentity(m)
    if (!primary_key) continue
    const prev = byKey.get(primary_key)
    if (!prev) {
      byKey.set(primary_key, { ...m })
      continue
    }
    const a = Math.floor(Number(prev.quantity ?? 0))
    const b = Math.floor(Number(m.quantity ?? 0))
    byKey.set(primary_key, {
      ...prev,
      quantity: a + b,
    })
  }
  return Array.from(byKey.values())
}

/**
 * Pure: normalized strict cart items (modifier fallback, drop non-positive, dedupe by identity).
 */
export function normalizeCart(cartItems: CartItem[]): CartItem[] {
  return cartItems.map((item) => ({
    ...item,
    selected_modifiers: (item.selected_modifiers ?? [])
      .map((group) => ({
        ...group,
        items: dedupeModifierGroupItems(
          group.items
            .map((mod) => ({
              ...mod,
              quantity: resolveModifierQuantity(mod, item.quantity),
            }))
            .filter((mod) => (mod.quantity ?? 0) > 0)
        ),
      }))
      .filter((g) => (g.items?.length ?? 0) > 0),
  }))
}

function cartLineToStrictCartItem(line: CartLine): CartItem {
  return {
    client_item_id: line.client_item_id,
    menu_item_id: String(line.menu_item_id),
    quantity: line.quantity,
    selected_modifiers: (line.selected_modifiers ?? []).map((g) => ({
      modifier_group_id: g.modifier_group_id,
      items: g.items.map((m) => ({
        child_menu_item_id: m.child_menu_item_id,
        modifier_group_item_id: m.modifier_group_item_id,
        quantity: m.quantity,
      })),
    })),
  }
}

function buildComboPreviewRows(
  line: CartLine,
  normalizedParent: CartItem,
  comboMode: BillingPreviewComboMode
): BillingPreviewV1CartItem[] {
  const parentRow: BillingPreviewV1CartItem = {
    client_item_id: normalizedParent.client_item_id,
    menu_item_id: normalizedParent.menu_item_id,
    quantity: normalizedParent.quantity,
    selected_modifiers: normalizedParent.selected_modifiers,
  }
  const combos = line.selectedCombos ?? []
  if (combos.length === 0) return [parentRow]

  if (comboMode === "structural") {
    return [
      {
        ...parentRow,
        combos: combos.map((c) => ({
          combo_group_id: c.combo_group_id,
          child_menu_item_id: c.child_menu_item_id,
          effective_price: c.effective_price,
        })),
      },
    ]
  }

  const rows: BillingPreviewV1CartItem[] = [parentRow]
  for (const c of combos) {
    rows.push({
      client_item_id: `${normalizedParent.client_item_id}:combo:${c.child_menu_item_id}`,
      menu_item_id: c.child_menu_item_id,
      quantity: normalizedParent.quantity,
      selected_modifiers: [],
    })
  }
  return rows
}

export type TransformCartToBillingPreviewOptions = {
  comboMode: BillingPreviewComboMode
}

/**
 * Cart lines → billing preview line items. **Pure:** no I/O, env, throws, logging, or input mutation.
 */
export function transformCartToBillingPreviewCartItems(
  lines: CartLine[],
  options: TransformCartToBillingPreviewOptions
): BillingPreviewV1CartItem[] {
  const strict = lines.map(cartLineToStrictCartItem)
  const normalized = normalizeCart(strict)
  const byClient = new Map(lines.map((l) => [l.client_item_id, l]))

  const out: BillingPreviewV1CartItem[] = []
  for (const n of normalized) {
    const line = byClient.get(n.client_item_id)
    if (!line) continue
    out.push(...buildComboPreviewRows(line, n, options.comboMode))
  }
  return out
}

export function transformCartToPricingInput(
  lines: CartLine[],
  options: TransformCartToBillingPreviewOptions
): BillingPreviewV1CartItem[] {
  return transformCartToBillingPreviewCartItems(lines, options)
}

/** Alias: cart lines → preview/order line shape (same transform as billing preview). */
export function transformCartToOrderItems(
  lines: CartLine[],
  options: TransformCartToBillingPreviewOptions
): BillingPreviewV1CartItem[] {
  return transformCartToBillingPreviewCartItems(lines, options)
}
